import { useEffect, useState } from 'react';
import { CategoryPillRow } from '../CategoryPillRow';
import { MyPacksButton } from './MyPacksButton';

/**
 * Scroll-state hook — true once `window.scrollY` passes the threshold,
 * false again when back near the top. Short-circuits with the same-value
 * setter so React doesn't re-render on every scroll frame.
 */
export function useIsScrolled(threshold = 8): boolean {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const update = () => setScrolled(window.scrollY > threshold);
    update();
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, [threshold]);
  return scrolled;
}

export interface HeaderCategory {
  id: string;
  label: string;
  iconSrc?: string;
}

export interface HeaderProps {
  categories: HeaderCategory[];
  activeCategoryId?: string;
  onCategorySelect?: (id: string) => void;
  onMyPacksClick?: () => void;
  className?: string;
}

/**
 * Floating glass header for the pack-detail / shop page.
 *
 * Outer `<header>` is full-width sticky so the glass / scroll-state
 * background bleeds edge-to-edge. The inner wrapper mirrors the page
 * content container (`mx-auto max-w-350` + `px-4 / @[600px]:px-6 /
 * @[900px]:px-8`) so pills and My Packs line up vertically with the
 * PackLayout grid below.
 *
 * Inner grid is `[minmax(0,1fr)] [auto]`: pills hug the left, My Packs
 * stays flush to the container's right edge. The pill wrapper is hidden
 * under 600px and the 1fr track collapses to 0, so the mobile layout
 * becomes `[empty] [My Packs]` with My Packs at the right edge.
 */
export function Header({
  categories,
  activeCategoryId,
  onCategorySelect,
  onMyPacksClick,
  className = '',
}: HeaderProps) {
  const scrolled = useIsScrolled(8);

  return (
    <header
      data-scrolled={scrolled || undefined}
      data-co-component="Header"
      className={
        '@container sticky top-0 z-40 ' +
        'py-5 ' +
        'transition-[background-color,backdrop-filter,border-color] duration-200 ease-out ' +
        (scrolled
          ? 'bg-black/40 backdrop-blur-md border-b border-white/10 '
          : 'border-b border-transparent ') +
        className
      }
    >
      {/* DS override layer: --co-Header-bg / --co-Header-text. Rendered as an
          absolute inset span so it doesn't interfere with scroll-state Tailwind
          background classes (inline style would win over class). When the CSS
          vars are unset the span is transparent and invisible. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-[1]"
        style={{ background: 'var(--co-Header-bg)', color: 'var(--co-Header-text)' }}
      />

      <div
        className="
          mx-auto max-w-350
          px-4 @[600px]:px-6 @[900px]:px-8
          grid grid-cols-[minmax(0,1fr)_auto] items-center gap-6
        "
      >
        <div className="hidden min-w-0 @[600px]:block">
          <CategoryPillRow
            categories={categories}
            centered={false}
            {...(activeCategoryId !== undefined ? { activeId: activeCategoryId } : {})}
            {...(onCategorySelect ? { onSelect: onCategorySelect } : {})}
          />
        </div>

        <MyPacksButton {...(onMyPacksClick ? { onClick: onMyPacksClick } : {})} />
      </div>
    </header>
  );
}
